import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { eventDealTermsApi } from "../../api";
import { QueryErrorBanner } from "../feedback/QueryFeedback";
import { MobileSelect } from "../ui/MobileSelect";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { isOwner } from "../../utils/roles";
import { queryKeys } from "../../queryKeys";
import type { DealType, EventDealTermsUpsert } from "../../types";
import { formatINR } from "../../utils/format";
import "./EventDealTermsPanel.css";

type Props = {
  eventId: string;
};

type FormState = {
  dealType: DealType;
  guaranteeInr: string;
  artistSplitPct: string;
  backendPct: string;
  promoterSharePct: string;
  notes: string;
};

const emptyForm = (): FormState => ({
  dealType: "guarantee",
  guaranteeInr: "",
  artistSplitPct: "",
  backendPct: "",
  promoterSharePct: "",
  notes: "",
});

const DEAL_OPTIONS: { value: DealType; label: string }[] = [
  { value: "guarantee", label: "Guarantee" },
  { value: "versus", label: "Versus" },
  { value: "co_promote", label: "Co-promote" },
  { value: "door_split", label: "Door split" },
];

export const EventDealTermsPanel: React.FC<Props> = ({ eventId }) => {
  const { token, user } = useAuth();
  // The backend gates deal-term writes to owners (403 otherwise); promoters get
  // a read-only view of their own row. Reflect that instead of letting the save
  // fail after the fact.
  const canEditDealTerms = isOwner(user);
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;

  const [terms, setTerms] = useState<FormState>(() => emptyForm());
  const [dirty, setDirty] = useState(false);

  const dealTermsQuery = useQuery({
    queryKey: eventId ? queryKeys.eventDealTerms(workspace, eventId) : ["eventDealTerms", "idle"],
    queryFn: () => eventDealTermsApi.get(eventId, token!),
    enabled: Boolean(eventId && token),
  });

  useEffect(() => {
    if (dealTermsQuery.data === undefined) return;
    const row = dealTermsQuery.data;
    setTerms(
      row
        ? {
            dealType: row.deal_type,
            guaranteeInr: row.guarantee_inr != null ? String(row.guarantee_inr) : "",
            artistSplitPct: row.artist_split_pct != null ? String(row.artist_split_pct) : "",
            backendPct: row.backend_pct != null ? String(row.backend_pct) : "",
            promoterSharePct: row.promoter_share_pct != null ? String(row.promoter_share_pct) : "",
            notes: row.notes ?? "",
          }
        : emptyForm(),
    );
    setDirty(false);
  }, [dealTermsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: EventDealTermsUpsert = {
        deal_type: terms.dealType,
        guarantee_inr: terms.guaranteeInr.trim() ? Number(terms.guaranteeInr) : null,
        artist_split_pct: terms.artistSplitPct.trim() ? Number(terms.artistSplitPct) : null,
        backend_pct: terms.backendPct.trim() ? Number(terms.backendPct) : null,
        promoter_share_pct: terms.promoterSharePct.trim() ? Number(terms.promoterSharePct) : null,
        notes: terms.notes.trim() || null,
      };
      return eventDealTermsApi.upsert(eventId, payload, token!);
    },
    onSuccess: async () => {
      setDirty(false);
      toast.success("Deal terms saved");
      await queryClient.invalidateQueries({ queryKey: queryKeys.eventDealTerms(workspace, eventId) });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save deal terms"),
  });

  const patch = (patchValue: Partial<FormState>) => {
    setTerms((prev) => ({ ...prev, ...patchValue }));
    setDirty(true);
  };

  const guaranteePreview =
    terms.guaranteeInr && Number.isFinite(Number(terms.guaranteeInr))
      ? formatINR(Number(terms.guaranteeInr))
      : null;

  return (
    <section className="event-deal-terms eventos-card" aria-label="Deal terms">
      <div className="event-deal-terms__head">
        <div>
          <div className="eventos-label">Deal terms</div>
          <p className="eventos-subtle event-deal-terms__lede">Guarantee, splits, and backend.</p>
        </div>
        {guaranteePreview ? <span className="app-shell-pill">{guaranteePreview} guarantee</span> : null}
      </div>

      {dealTermsQuery.isError ? (
        <QueryErrorBanner
          message={dealTermsQuery.error instanceof Error ? dealTermsQuery.error.message : "Failed to load deal terms"}
          onRetry={() => void dealTermsQuery.refetch()}
        />
      ) : null}

      <div className="eventos-form-grid">
        <label className="eventos-field">
          <span className="eventos-field-label">Deal structure</span>
          <MobileSelect
            label="Deal structure"
            className="eventos-select"
            value={terms.dealType}
            onChange={(v) => patch({ dealType: v as DealType })}
            disabled={dealTermsQuery.isPending}
            options={DEAL_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Guarantee (INR)</span>
          <input
            className="eventos-input"
            type="number"
            min={0}
            value={terms.guaranteeInr}
            onChange={(e) => patch({ guaranteeInr: e.target.value })}
            placeholder="e.g. 500000"
            disabled={dealTermsQuery.isPending}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Artist split %</span>
          <input
            className="eventos-input"
            type="number"
            min={0}
            max={100}
            value={terms.artistSplitPct}
            onChange={(e) => patch({ artistSplitPct: e.target.value })}
            placeholder="e.g. 85"
            disabled={dealTermsQuery.isPending}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Backend %</span>
          <input
            className="eventos-input"
            type="number"
            min={0}
            max={100}
            value={terms.backendPct}
            onChange={(e) => patch({ backendPct: e.target.value })}
            placeholder="e.g. 15"
            disabled={dealTermsQuery.isPending}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Promoter share %</span>
          <input
            className="eventos-input"
            type="number"
            min={0}
            max={100}
            value={terms.promoterSharePct}
            onChange={(e) => patch({ promoterSharePct: e.target.value })}
            placeholder="e.g. 50"
            disabled={dealTermsQuery.isPending}
          />
        </label>
      </div>
      <label className="eventos-field" style={{ marginTop: 12 }}>
        <span className="eventos-field-label">Deal notes</span>
        <textarea
          className="eventos-input"
          rows={3}
          value={terms.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder="Walkout, bonus tiers, marketing fund, exclusivity…"
          disabled={dealTermsQuery.isPending}
        />
      </label>
      <div className="app-form-actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="eventos-primary-btn"
          onClick={() => saveMutation.mutate()}
          disabled={!canEditDealTerms || !dirty || saveMutation.isPending}
        >
          Save deal terms
        </button>
        {!canEditDealTerms ? (
          <span className="eventos-subtle">Only owners can change deal terms.</span>
        ) : dirty ? (
          <span className="eventos-subtle">Unsaved changes</span>
        ) : null}
      </div>
    </section>
  );
};
